import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, Shield, Zap, TrendingUp, Users, FileText, Wallet, Bell,
  CheckCircle2, BarChart3, Lock, Clock, ArrowRight, Terminal,
  CreditCard, Smartphone, Database, LineChart, ChevronRight, Award,
  FileCheck, Activity, PieChart, Target, Flame, Gauge,
} from "lucide-react";

// ============= Motion variants =============
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7 } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const slideLeft = {
  hidden: { opacity: 0, x: 32 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

// ============= NavBar =============
const NavBar = ({ onDaemon, onPortal }) => (
  <motion.nav
    initial={{ y: -80, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.5, ease: "easeOut" }}
    className="fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200/60 shadow-sm"
    data-testid="landing-navbar"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center shadow-md">
          <Building2 className="w-5 h-5 text-yellow-400" strokeWidth={2.2} />
        </div>
        <div className="leading-tight">
          <p className="font-extrabold text-slate-900 text-[15px] tracking-tight">SIRET</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Sistema de Recaudación Tributaria</p>
        </div>
      </div>
      <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-700">
        <a href="#modulos" className="hover:text-blue-800 transition">Módulos</a>
        <a href="#dashboard" className="hover:text-blue-800 transition">Dashboard</a>
        <a href="#beneficios" className="hover:text-blue-800 transition">Beneficios</a>
        <a href="#seguridad" className="hover:text-blue-800 transition">Seguridad</a>
      </div>
      <div className="flex items-center gap-2">
        {/* <button
          onClick={onPortal}
          className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-blue-900 hover:bg-blue-50 transition"
          data-testid="nav-portal-ciudadano-btn"
        >
          <Users className="w-4 h-4" /> Portal Online
        </button> */}
        <button
          onClick={onDaemon}
          className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-blue-800 to-blue-600 hover:from-blue-900 hover:to-blue-700 shadow-md hover:shadow-lg transition-all"
          data-testid="nav-daemon-btn"
        >
          <Terminal className="w-4 h-4" /> Cuenta Demo
        </button>
      </div>
    </div>
  </motion.nav>
);

// ============= Hero (widget removed, centered content) =============
const Hero = ({ onPayCTA, onDaemon }) => (
  <section
    className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden"
    style={{
      background: `
        radial-gradient(circle at 18% 80%, rgba(250,204,21,0.08) 0%, transparent 55%),
        radial-gradient(circle at 82% 20%, rgba(16,185,129,0.14) 0%, transparent 55%),
        linear-gradient(135deg, #0F172A 0%, #1E3A8A 55%, #1D4ED8 100%)
      `,
    }}
    data-testid="landing-hero"
  >
    {/* Animated grid texture */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.06 }}
      transition={{ duration: 1.2 }}
      className="absolute inset-0"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }}
    />
    {/* Floating orbs */}
    <motion.div
      aria-hidden
      animate={{ y: [0, -18, 0] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      className="absolute top-28 right-[14%] w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl"
    />
    <motion.div
      aria-hidden
      animate={{ y: [0, 20, 0] }}
      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      className="absolute bottom-10 left-[8%] w-80 h-80 rounded-full bg-blue-400/10 blur-3xl"
    />

    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      className="relative max-w-5xl mx-auto px-5 md:px-8 text-center text-white"
    >
      <motion.div
        variants={fadeUp}
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs font-medium text-blue-100 mb-7"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        Plataforma oficial de recaudación municipal · v2026
      </motion.div>

      <motion.h1
        variants={fadeUp}
        className="font-extrabold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mb-6"
      >
        <span className="block">Transparencia, rapidez</span>
        <span className="block bg-gradient-to-r from-yellow-300 via-emerald-300 to-cyan-300 bg-clip-text text-transparent">
          y disponibilidad 24/7.
        </span>
      </motion.h1>

      <motion.p
        variants={fadeUp}
        className="text-lg md:text-xl text-blue-100/90 max-w-2xl mx-auto mb-10 leading-relaxed"
      >
        SIRET digitaliza la tributación municipal con un único propósito: poner la gestión pública al servicio del ciudadano, con datos en tiempo real y auditables.
      </motion.p>

      <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center">
        <motion.button
          whileHover={{ y: -3, scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onPayCTA}
          className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold shadow-xl shadow-emerald-900/40 transition-colors"
          data-testid="hero-pagar-impuestos-btn"
        >
          <Wallet className="w-5 h-5" />
          Pagar mis Impuestos
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </motion.button>
        <motion.button
          whileHover={{ y: -3, scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onDaemon}
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/25 backdrop-blur transition-colors"
          data-testid="hero-daemon-btn"
        >
          <Terminal className="w-5 h-5" /> Acceder — Demo
        </motion.button>
      </motion.div>

      <motion.div
        variants={fadeUp}
        className="mt-12 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-xs text-blue-100/80"
      >
        <span className="inline-flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-300" /> Cifrado SHA-256 encadenado</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-300" /> Auditoría forense inmutable</span>
        <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-emerald-300" /> MFA & Refresh Tokens</span>
      </motion.div>
    </motion.div>
  </section>
);

// ============= Stats strip =============
const Stats = () => {
  const items = [
    { icon: Shield, label: "Seguridad certificada", value: "WCAG · CSP · HSTS", color: "from-blue-800 to-blue-600" },
    { icon: Zap, label: "Rapidez de procesamiento", value: "< 200 ms promedio", color: "from-emerald-600 to-emerald-400" },
    { icon: Activity, label: "Transparencia total", value: "Audit log inmutable", color: "from-amber-500 to-yellow-400" },
    { icon: Clock, label: "Disponibilidad 24/7", value: "99.95% uptime", color: "from-indigo-700 to-sky-500" },
  ];
  return (
    <section className="relative -mt-12 md:-mt-16 max-w-7xl mx-auto px-5 md:px-8" data-testid="landing-stats">
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
      >
        {items.map((s) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.label}
              variants={scaleIn}
              whileHover={{ y: -4 }}
              className="rounded-2xl bg-white border border-slate-200 shadow-lg shadow-slate-200/60 p-5 hover:shadow-xl transition-shadow"
            >
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-3 shadow-md`}>
                <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
              </div>
              <p className="text-xs text-slate-500 font-medium">{s.label}</p>
              <p className="text-base font-bold text-slate-900 mt-0.5">{s.value}</p>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
};

// ============= Dashboard Showcase =============
const AnimatedBar = ({ height, delay, color = "bg-gradient-to-t from-blue-700 to-emerald-400" }) => (
  <motion.div
    initial={{ height: 0 }}
    whileInView={{ height }}
    viewport={{ once: true }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`w-full rounded-t-md ${color}`}
  />
);

const Counter = ({ from = 0, to, suffix = "", prefix = "", delay = 0 }) => (
  <motion.span
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay }}
  >
    <motion.span
      initial={{ scale: 0.9 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {prefix}
      <motion.span
        initial={{ color: "#94a3b8" }}
        whileInView={{ color: "#0f172a" }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay }}
      >
        {to.toLocaleString("es-VE")}
      </motion.span>
      {suffix}
    </motion.span>
  </motion.span>
);

const DashboardShowcase = () => {
  const kpis = [
    { label: "Recaudado hoy", value: "184.320", prefix: "Bs. ", trend: "+12.4%", color: "emerald", icon: Wallet },
    { label: "Contribuyentes activos", value: "4.287", trend: "+2.1%", color: "blue", icon: Users },
    { label: "Planillas emitidas", value: "612", trend: "+8.7%", color: "indigo", icon: FileText },
    { label: "Morosidad detectada", value: "3.1", suffix: "%", trend: "-0.8%", color: "rose", icon: TrendingUp },
  ];
  const barHeights = ["38%", "62%", "48%", "85%", "70%", "95%", "58%"];
  const days = ["L", "M", "M", "J", "V", "S", "D"];

  return (
    <section
      id="dashboard"
      className="relative py-20 md:py-28 overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #0F172A 0%, #1E293B 100%)",
      }}
      data-testid="landing-dashboard-showcase"
    >
      {/* subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid lg:grid-cols-12 gap-10 items-center"
        >
          {/* Left narrative */}
          <div className="lg:col-span-4 text-white">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-xs font-semibold text-emerald-300 mb-5">
              <Activity className="w-3.5 h-3.5" /> Tiempo real
            </motion.div>
            <motion.h2
              variants={fadeUp}
              className="font-extrabold text-3xl md:text-4xl leading-tight tracking-tight mb-5"
            >
              Dashboard ejecutivo <br />
              con <span className="text-emerald-300">métricas vivas</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-blue-100/80 text-base leading-relaxed mb-6">
              Cada bolívar recaudado, cada morosidad, cada tendencia se actualiza al instante. Decisiones informadas, gobernanza basada en datos.
            </motion.p>
            <motion.ul variants={stagger} className="space-y-3">
              {[
                { icon: Target, text: "KPIs de recaudación por módulo" },
                { icon: PieChart, text: "Distribución por código presupuestario" },
                { icon: Flame, text: "Alertas de morosidad automáticas" },
                { icon: Gauge, text: "Proyecciones y cumplimiento de metas" },
              ].map((it) => {
                const Icon = it.icon;
                return (
                  <motion.li variants={fadeUp} key={it.text} className="flex items-center gap-3 text-blue-100/90">
                    <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-emerald-300" strokeWidth={2.2} />
                    </span>
                    <span className="text-sm">{it.text}</span>
                  </motion.li>
                );
              })}
            </motion.ul>
          </div>

          {/* Right dashboard mockup */}
          <motion.div variants={slideLeft} className="lg:col-span-8">
            <div className="relative rounded-3xl bg-white/[0.04] backdrop-blur border border-white/10 p-6 md:p-8 shadow-2xl shadow-blue-950/50">
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 mb-5">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                <div className="ml-4 px-3 py-1 rounded-md bg-white/5 text-[11px] text-blue-100/60 font-mono">siret.gob.ve / dashboard</div>
              </div>

              {/* KPI cards */}
              <motion.div variants={stagger} className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {kpis.map((k, i) => {
                  const Icon = k.icon;
                  const ring = {
                    emerald: "from-emerald-500/20 text-emerald-300 border-emerald-400/30",
                    blue: "from-blue-500/20 text-blue-200 border-blue-400/30",
                    indigo: "from-indigo-500/20 text-indigo-200 border-indigo-400/30",
                    rose: "from-rose-500/20 text-rose-200 border-rose-400/30",
                  }[k.color];
                  const trendColor = k.trend.startsWith("-") ? "text-amber-300" : "text-emerald-300";
                  return (
                    <motion.div
                      key={k.label}
                      variants={scaleIn}
                      className={`rounded-xl bg-gradient-to-br ${ring} border p-4 backdrop-blur`}
                    >
                      <div className="flex items-center justify-between mb-2.5">
                        <Icon className="w-4 h-4 opacity-80" />
                        <span className={`text-[10px] font-bold ${trendColor}`}>{k.trend}</span>
                      </div>
                      <p className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">{k.label}</p>
                      <p className="text-xl font-extrabold text-white mt-1">
                        <Counter to={parseFloat(k.value.replace(/\./g, "").replace(",", "."))} prefix={k.prefix || ""} suffix={k.suffix || ""} delay={0.1 * i} />
                      </p>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Chart area */}
              <div className="grid md:grid-cols-3 gap-4">
                {/* Bar chart */}
                <motion.div variants={fadeUp} className="md:col-span-2 rounded-xl bg-black/20 border border-white/10 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-xs text-blue-100/60 font-semibold uppercase tracking-wider">Recaudación semanal</p>
                      <p className="text-lg font-extrabold text-white mt-0.5">Bs. <Counter to={1284320} delay={0.2} /></p>
                    </div>
                    <span className="px-2 py-1 text-[10px] font-semibold rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">+14.2%</span>
                  </div>
                  <div className="flex items-end gap-2.5 h-28 border-b border-white/10 pb-0.5">
                    {barHeights.map((h, i) => (
                      <div key={i} className="flex-1 flex items-end">
                        <AnimatedBar height={h} delay={0.08 * i} />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2 text-[11px] text-blue-100/50 font-mono">
                    {days.map((d, i) => <span key={i} className="flex-1 text-center">{d}</span>)}
                  </div>
                </motion.div>

                {/* Donut mock */}
                <motion.div variants={fadeUp} className="rounded-xl bg-black/20 border border-white/10 p-5 flex flex-col">
                  <p className="text-xs text-blue-100/60 font-semibold uppercase tracking-wider mb-3">Por módulo</p>
                  <div className="relative flex-1 flex items-center justify-center">
                    <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90">
                      <circle cx="60" cy="60" r="48" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="16" />
                      <motion.circle
                        cx="60" cy="60" r="48" fill="none"
                        stroke="#10B981" strokeWidth="16" strokeLinecap="round"
                        strokeDasharray="301.6" initial={{ strokeDashoffset: 301.6 }}
                        whileInView={{ strokeDashoffset: 85 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
                      />
                      <motion.circle
                        cx="60" cy="60" r="48" fill="none"
                        stroke="#FACC15" strokeWidth="16" strokeLinecap="round"
                        strokeDasharray="301.6" initial={{ strokeDashoffset: 301.6 }}
                        whileInView={{ strokeDashoffset: 210 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.8 }}
                        style={{ transformOrigin: "center", rotate: "216deg" }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-[10px] text-blue-100/60">Recaudado</p>
                      <p className="text-xl font-extrabold text-white">72%</p>
                    </div>
                  </div>
                  <div className="space-y-1.5 mt-2 text-[11px]">
                    <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-blue-100/80"><span className="w-2 h-2 rounded-full bg-emerald-400" />Catastro</span><span className="font-mono text-white">48%</span></div>
                    <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-blue-100/80"><span className="w-2 h-2 rounded-full bg-yellow-400" />LAE</span><span className="font-mono text-white">24%</span></div>
                    <div className="flex items-center justify-between"><span className="flex items-center gap-1.5 text-blue-100/80"><span className="w-2 h-2 rounded-full bg-white/20" />Otros</span><span className="font-mono text-white">28%</span></div>
                  </div>
                </motion.div>
              </div>

              {/* Bottom pulse */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 1.2 }}
                className="mt-5 flex items-center justify-between px-4 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-400/20"
              >
                <div className="flex items-center gap-2">
                  <span className="relative flex w-2.5 h-2.5">
                    <span className="animate-ping absolute inset-0 rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative rounded-full w-2.5 h-2.5 bg-emerald-400" />
                  </span>
                  <span className="text-xs text-emerald-200 font-semibold">Sincronizando en tiempo real · 8 módulos conectados</span>
                </div>
                <span className="text-[10px] font-mono text-emerald-200/70">02:45:12</span>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= Problema / Solución =============
const ProblemaSolucion = () => (
  <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="problema-solucion">
    <motion.div
      variants={stagger}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      className="grid md:grid-cols-2 gap-8 md:gap-16 items-center"
    >
      <motion.div variants={fadeUp}>
        <p className="text-xs font-bold uppercase tracking-widest text-rose-600 mb-3">El Reto</p>
        <h2 className="font-extrabold text-3xl md:text-4xl text-slate-900 leading-tight mb-5 tracking-tight">
          La recaudación manual cuesta <span className="text-rose-600">tiempo, dinero y confianza.</span>
        </h2>
        <ul className="space-y-3 text-slate-600">
          {[
            "Expedientes en papel, duplicados y pérdida de documentación.",
            "Procesos lentos en ventanilla y largas filas para el ciudadano.",
            "Falta de trazabilidad y exposición a fraudes o irregularidades.",
            "Reportes elaborados a mano que tardan semanas en consolidarse.",
          ].map((t) => (
            <li key={t} className="flex gap-3 items-start">
              <span className="mt-1 w-5 h-5 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
              </span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </motion.div>
      <motion.div variants={fadeUp}>
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">La Solución</p>
        <h2 className="font-extrabold text-3xl md:text-4xl text-slate-900 leading-tight mb-5 tracking-tight">
          SIRET automatiza, audita y <span className="text-emerald-600">digitaliza</span> cada gestión.
        </h2>
        <ul className="space-y-3 text-slate-600">
          {[
            { t: "Plataforma unificada", d: "Catastro, vehículos, publicidad, LAE, ambulantes y más en un solo sistema." },
            { t: "Pagos en línea seguros", d: "Integración de medios electrónicos con comprobantes verificables por QR." },
            { t: "Auditoría forense", d: "Cada acción firmada con hash SHA-256 encadenado — imposible alterar el pasado." },
            { t: "Datos en tiempo real", d: "Dashboard ejecutivo con KPIs, morosidad y proyecciones al instante." },
          ].map((it) => (
            <li key={it.t} className="flex gap-3 items-start">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900">{it.t}</p>
                <p className="text-sm">{it.d}</p>
              </div>
            </li>
          ))}
        </ul>
      </motion.div>
    </motion.div>
  </section>
);

// ============= Módulos =============
const Modulos = () => {
  const mods = [
    { icon: Building2, name: "Catastro", desc: "Fichas, valoración y boletines de cálculo inmobiliario.", color: "bg-blue-50 text-blue-800" },
    { icon: FileCheck, name: "Desarrollo Urbano", desc: "Conformidades de uso, habitabilidad y solvencias.", color: "bg-indigo-50 text-indigo-800" },
    { icon: Award, name: "Publicidad y Vallas", desc: "Avisos, tasas trimestrales y clasificadores.", color: "bg-purple-50 text-purple-800" },
    { icon: TrendingUp, name: "Vehículos", desc: "Registro, cálculo y recaudación anual.", color: "bg-sky-50 text-sky-800" },
    { icon: Users, name: "Vendedores Ambulantes", desc: "Registro, categorías y cobro mensual.", color: "bg-orange-50 text-orange-800" },
    { icon: Wallet, name: "LAE y Liquidaciones", desc: "Licencias de Actividad Económica y declaraciones.", color: "bg-emerald-50 text-emerald-800" },
    { icon: FileText, name: "Recaudación y Caja", desc: "Comprobantes, conciliación y cierre diario.", color: "bg-teal-50 text-teal-800" },
    { icon: LineChart, name: "Reportes Gerenciales", desc: "Mayor de impuesto, morosidad, recaudación por código.", color: "bg-rose-50 text-rose-800" },
  ];
  return (
    <section
      id="modulos"
      className="bg-slate-50 border-y border-slate-200 py-20 md:py-28"
      data-testid="landing-modulos"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-12 max-w-2xl mx-auto"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-3">Módulos principales</p>
          <h2 className="font-extrabold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Un ecosistema completo para la tributación municipal
          </h2>
          <p className="text-slate-600 mt-3 text-base">
            Cada módulo opera de forma independiente pero converge en un único flujo de caja y auditoría.
          </p>
        </motion.div>
        <motion.div
          variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {mods.map((m) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.name}
                variants={scaleIn}
                whileHover={{ y: -6 }}
                className="group rounded-2xl bg-white border border-slate-200 p-5 hover:border-blue-300 hover:shadow-xl transition-all"
                data-testid={`modulo-card-${m.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className={`inline-flex w-11 h-11 rounded-xl ${m.color} items-center justify-center mb-3`}>
                  <Icon className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <h3 className="font-bold text-slate-900 text-[15px] mb-1.5">{m.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{m.desc}</p>
                <div className="mt-3 text-xs font-semibold text-blue-700 opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-1">
                  Conocer más <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ============= Beneficios =============
const Beneficios = () => {
  const admin = [
    { icon: BarChart3, t: "Dashboard en tiempo real", d: "KPIs de recaudación, morosidad y proyecciones actualizados al segundo." },
    { icon: Shield, t: "Reducción de evasión", d: "Cruces automáticos entre módulos detectan contribuyentes morosos." },
    { icon: Database, t: "Reportes automatizados", d: "Mayor de impuesto, recaudación por código presupuestario, auditoría." },
    { icon: Lock, t: "Control de acceso granular", d: "Roles, permisos, MFA, API Keys con scopes y sesiones activas." },
  ];
  const ciudadano = [
    { icon: CreditCard, t: "Pagos en línea 24/7", d: "Pague sus tributos desde cualquier dispositivo con comprobante verificable." },
    { icon: Smartphone, t: "Trámites sin filas", d: "Declare, reporte pagos y consulte solvencias desde su celular." },
    { icon: Bell, t: "Notificaciones proactivas", d: "Avisos de vencimiento, confirmaciones de pago y alertas de obligaciones." },
    { icon: FileCheck, t: "Historial transparente", d: "Acceda a todos sus comprobantes y estados de cuenta en un solo lugar." },
  ];
  return (
    <section id="beneficios" className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="landing-beneficios">
      <motion.div
        variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
        className="grid lg:grid-cols-2 gap-10"
      >
        {/* Admin */}
        <motion.div variants={fadeUp} className="rounded-3xl bg-gradient-to-br from-blue-900 to-blue-700 text-white p-8 md:p-10 shadow-xl shadow-blue-900/20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-semibold text-blue-100 mb-5 border border-white/20">
            <Building2 className="w-3.5 h-3.5" /> Para la Administración
          </div>
          <h3 className="font-extrabold text-2xl md:text-3xl mb-6 tracking-tight">
            Gobernanza basada en datos confiables
          </h3>
          <div className="grid sm:grid-cols-2 gap-5">
            {admin.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.t}>
                  <div className="w-10 h-10 rounded-lg bg-yellow-400/15 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-yellow-300" strokeWidth={2.2} />
                  </div>
                  <p className="font-semibold text-[15px] mb-1">{b.t}</p>
                  <p className="text-sm text-blue-100/85 leading-relaxed">{b.d}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Ciudadano */}
        <motion.div variants={fadeUp} className="rounded-3xl bg-white border border-slate-200 p-8 md:p-10 shadow-xl shadow-slate-200/60">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700 mb-5 border border-emerald-100">
            <Users className="w-3.5 h-3.5" /> Para el Ciudadano
          </div>
          <h3 className="font-extrabold text-2xl md:text-3xl mb-6 text-slate-900 tracking-tight">
            Una experiencia tributaria sin fricciones
          </h3>
          <div className="grid sm:grid-cols-2 gap-5">
            {ciudadano.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.t}>
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-emerald-700" strokeWidth={2.2} />
                  </div>
                  <p className="font-semibold text-[15px] mb-1 text-slate-900">{b.t}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{b.d}</p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= Confianza =============
const Confianza = () => {
  const certs = [
    { t: "Ciberseguridad de alto nivel", d: "HSTS, CSP estricto, MFA TOTP, refresh tokens con rotación, Frame Killer JS." },
    { t: "Auditoría forense", d: "Cada operación queda firmada con hash SHA-256 encadenado: cualquier alteración es detectable." },
    { t: "Cumplimiento normativo", d: "Adaptado a la normativa tributaria municipal venezolana y a buenas prácticas OWASP." },
    { t: "Accesibilidad (WCAG)", d: "Tipografías legibles ≥ 16 px, contraste alto y navegación por teclado." },
  ];
  return (
    <section
      id="seguridad"
      className="relative py-20 md:py-28 overflow-hidden"
      style={{ background: "linear-gradient(180deg, #F8FAFC 0%, #EEF2FF 100%)" }}
      data-testid="landing-confianza"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
          className="text-center mb-12 max-w-2xl mx-auto"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-3">Confianza institucional</p>
          <h2 className="font-extrabold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Construido con estándares de seguridad de grado financiero
          </h2>
        </motion.div>
        <motion.div
          variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {certs.map((c) => (
            <motion.div variants={scaleIn} key={c.t} whileHover={{ y: -4 }}
              className="rounded-2xl bg-white p-6 border border-slate-200 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-800 to-emerald-600 flex items-center justify-center mb-4 shadow-md">
                <Shield className="w-5 h-5 text-white" strokeWidth={2.2} />
              </div>
              <p className="font-bold text-slate-900 text-[15px] mb-1.5">{c.t}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============= CTA Final =============
const CTAFinal = ({ onDaemon, onPortal }) => (
  <section
    className="relative py-20 md:py-28 overflow-hidden"
    style={{
      background:
        "radial-gradient(circle at 50% 120%, rgba(16,185,129,0.25) 0%, transparent 60%), linear-gradient(135deg, #0F172A 0%, #1E3A8A 100%)",
    }}
    data-testid="landing-cta-final"
  >
    <motion.div
      variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
      className="max-w-4xl mx-auto px-5 md:px-8 text-center text-white"
    >
      <motion.h2 variants={fadeUp} className="font-extrabold text-3xl md:text-5xl leading-tight tracking-tight mb-6">
        El municipio que usted imagina, <br className="hidden md:block" />
        <span className="text-emerald-300">empieza con una recaudación justa.</span>
      </motion.h2>
      <motion.p variants={fadeUp} className="text-blue-100/85 text-base md:text-lg max-w-2xl mx-auto mb-10">
        Acceda al sistema y descubra cómo la transparencia puede transformar la gestión pública.
      </motion.p>
      <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center">
        <motion.button
          whileHover={{ y: -3, scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={onDaemon}
          className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold shadow-xl shadow-emerald-900/40 transition-colors"
          data-testid="final-daemon-btn"
        >
          <Terminal className="w-5 h-5" /> Ingresar al Demo
          <ArrowRight className="w-4 h-4" />
        </motion.button>
        <motion.button
          whileHover={{ y: -3, scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={onPortal}
          className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold border border-white/25 backdrop-blur transition-colors"
          data-testid="final-portal-btn"
        >
          <Users className="w-5 h-5" /> Ir al Portal Online
        </motion.button>
      </motion.div>
    </motion.div>
  </section>
);

// ============= Footer =============
const Footer = () => (
  <footer className="bg-slate-950 text-slate-400 py-10" data-testid="landing-footer">
    <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center">
          <Building2 className="w-4 h-4 text-yellow-400" />
        </div>
        <span className="text-white font-bold">SIRET</span>
        <span className="hidden md:inline">·</span>
        <span className="hidden md:inline">Sistema de Recaudación Tributaria Municipal</span>
      </div>
      <p className="text-xs">© {new Date().getFullYear()} — Todos los derechos reservados · Venezuela</p>
    </div>
  </footer>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const goLogin = () => navigate("/login");
  const goDemon = () => {
    window.location.href = "https://demo.siret360.com";
  };

  return (
    <div
      className="min-h-screen bg-white font-sans text-slate-900"
      style={{ fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="landing-page"
    >
      <NavBar onDaemon={goDemon} onPortal={goLogin} />
      <Hero onPayCTA={goLogin} onDaemon={goDemon} />
      <Stats />
      <DashboardShowcase />
      <ProblemaSolucion />
      <Modulos />
      <Beneficios />
      <Confianza />
      <CTAFinal onDaemon={goDemon} onPortal={goLogin} />
      <Footer />
    </div>
  );
}
